import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, Sparkles, BookOpen, FileText, Map as MapIcon,
  Bookmark, Clock, Flag, Globe, Landmark, Scale, Cpu
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import AppShell from '@/components/AppShell';
import { gradeAccent } from '@/lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map grade -> subject filter (curriculum browse routing)
const ERAS = [
  { label: 'Ancient Civilizations', subtitle: '3500 BCE — 500 CE', grade: 9, color: '#7A93AC' },
  { label: 'Medieval & Renaissance', subtitle: '500 — 1600', grade: 9, color: '#9CAFC2' },
  { label: 'Age of Revolutions', subtitle: '1600 — 1850', grade: 10, color: '#7A93AC' },
  { label: 'Industrial Era', subtitle: '1850 — 1920', grade: 11, color: '#9CAFC2' },
  { label: 'World Wars & Cold War', subtitle: '1914 — 1991', grade: 11, color: '#7A93AC' },
  { label: 'Contemporary World', subtitle: '1991 — Present', grade: 12, color: '#9CAFC2' },
];

const SUBJECT_HUBS = [
  { label: 'US History', icon: Flag, path: '/curriculum/us-history', desc: 'Founding documents through modern America' },
  { label: 'World History', icon: Globe, path: '/curriculum/world-history', desc: 'Ancient civilizations to globalization' },
  { label: 'Civics', icon: Landmark, path: '/curriculum/civics', desc: 'Rights, participation, citizenship' },
  { label: 'Government', icon: Scale, path: '/curriculum/government', desc: 'Branches, federalism, policy' },
];

const ModuleCard = ({ title, viewAllPath, children, testid }) => {
  const navigate = useNavigate();
  return (
    <section
      className="bg-[#F9F8F4] border border-[#EBE7E0] rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
      data-testid={testid}
    >
      <header className="flex items-baseline justify-between mb-5">
        <h3 className="font-serif-display text-xl text-[#14213D] tracking-tight">{title}</h3>
        {viewAllPath && (
          <button
            onClick={() => navigate(viewAllPath)}
            className="text-xs text-[#7A93AC] hover:text-[#14213D] font-medium tracking-wide inline-flex items-center gap-1"
          >
            View all <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </header>
      {children}
    </section>
  );
};

const ResourceMiniCard = ({ unit, onOpen }) => {
  const accent = gradeAccent(unit.grade_level);
  return (
  <button
    onClick={onOpen}
    className="group text-left w-full bg-white border border-[#EBE7E0] rounded-md p-4 hover:-translate-y-0.5 hover:shadow-md hover:border-[#14213D]/20 transition-all relative overflow-hidden"
    data-testid={`recent-unit-${unit.id}`}
  >
    <div className="absolute top-0 left-0 w-full h-0.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: accent.hex }} />
    <div className="absolute top-0 left-0 w-1 h-full" style={{ background: accent.hex, opacity: 0.7 }} />
    <p className="text-[11px] uppercase tracking-[0.15em] font-semibold mb-1.5 pl-1" style={{ color: accent.hex }}>{accent.subject} · Grade {unit.grade_level}</p>
    <h4 className="font-serif-display text-base text-[#14213D] leading-tight mb-2 line-clamp-2 pl-1">{unit.title}</h4>
    <div className="flex items-center gap-2 mb-2 flex-wrap pl-1">
      <span className="bg-[#E2E8F0]/60 text-[#2D3748] text-[11px] px-2 py-0.5 rounded">Unit {unit.unit_number}</span>
      {unit.estimated_weeks && (
        <span className="bg-[#E2E8F0]/60 text-[#2D3748] text-[11px] px-2 py-0.5 rounded inline-flex items-center gap-1">
          <Clock className="h-2.5 w-2.5" /> {unit.estimated_weeks}w
        </span>
      )}
    </div>
    <p className="text-xs text-[#5A6577] line-clamp-2 leading-relaxed pl-1">{unit.description}</p>
    <span className="text-xs font-medium text-[#14213D] mt-3 inline-flex items-center gap-1 group-hover:text-[#D4AF37] transition-colors pl-1">
      Open lesson <ArrowRight className="h-3 w-3" />
    </span>
  </button>
  );
};

export default function AtlasDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [allUnits, setAllUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadUnits = useCallback(async () => {
    try {
      const results = await Promise.all(
        [7, 8, 9, 10, 11, 12].map(g => axios.get(`${API}/units/grade/${g}`).then(r => r.data).catch(() => []))
      );
      setAllUnits(results.flat());
    } catch (_e) {
      setAllUnits([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUnits(); }, [loadUnits]);

  const recentUnits = useMemo(() => allUnits.slice(0, 6), [allUnits]);
  const featuredUnit = useMemo(() => allUnits[0] || null, [allUnits]);
  const stats = useMemo(() => ({
    units: allUnits.length,
    grades: 6,
    sources: 957,
  }), [allUnits]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        {/* Greeting header */}
        <div className="mb-8" data-testid="dashboard-greeting">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#7A93AC] font-semibold mb-1.5">Welcome back</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[#14213D] tracking-tight">
            {greeting}, {user?.name?.split(' ')[0] || 'Teacher'}.
          </h1>
          <p className="text-sm text-[#5A6577] mt-2 max-w-xl">
            Everything you need to plan, teach, and assess your social studies units — organized as a single intelligent archive.
          </p>
        </div>

        {/* Top row — Featured + AI assistant */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
          {/* Featured Resource */}
          <div className="lg:col-span-8">
            {(() => { const accent = featuredUnit ? gradeAccent(featuredUnit.grade_level) : null; return (
            <div
              className="relative h-full min-h-[280px] rounded-lg overflow-hidden border border-[#EBE7E0] shadow-sm"
              style={{ background: accent
                ? `linear-gradient(135deg, ${accent.hex} 0%, #1D2D50 60%, #14213D 100%)`
                : 'linear-gradient(135deg, #14213D, #1D2D50)' }}
              data-testid="featured-resource"
            >
              <div className="absolute inset-0 opacity-20"
                style={{ backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/e079eb76-7fdc-4c18-ab8a-6dab3e979ce5/images/c5185e42f8768f4b17c1459b69c0c0a00be72cfd693ddba5dba436f67d0d7c3b.png')", backgroundSize: 'cover', mixBlendMode: 'overlay' }} />
              <div className="relative p-8 sm:p-10 h-full flex flex-col">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-3">
                  Featured Lesson{accent ? ` · ${accent.subject}` : ''}
                </p>
                {featuredUnit ? (
                  <>
                    <h2 className="font-serif-display text-2xl sm:text-3xl text-[#FDFBF7] tracking-tight leading-tight mb-3 max-w-xl">
                      {featuredUnit.title}
                    </h2>
                    <p className="text-sm text-[#FDFBF7]/70 max-w-xl leading-relaxed mb-6 line-clamp-3">
                      {featuredUnit.description}
                    </p>
                    <div className="flex items-center gap-3 mt-auto">
                      <button
                        onClick={() => navigate(`/unit/${featuredUnit.id}`)}
                        className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C5A028] text-[#14213D] text-sm font-semibold px-5 py-2.5 rounded-md transition-colors"
                        data-testid="featured-open-btn"
                      >
                        Open lesson <ArrowRight className="h-4 w-4" />
                      </button>
                      <span className="text-xs text-[#FDFBF7]/50 tracking-wide">Grade {featuredUnit.grade_level} · Unit {featuredUnit.unit_number}</span>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-[#FDFBF7]/60">Loading featured lesson…</p>
                )}
              </div>
            </div>
            ); })()}
          </div>

          {/* AI Teaching Assistant card */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            <div
              className="rounded-lg border border-[#EBE7E0] bg-[#F9F8F4] p-6 flex-1"
              data-testid="ai-assistant-card"
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                <h3 className="font-serif-display text-lg text-[#14213D] tracking-tight">AI Teaching Assistant</h3>
              </div>
              <p className="text-sm text-[#5A6577] leading-relaxed mb-4">
                Generate lesson plans, rubrics, and primary-source excerpts — calibrated to your standards.
              </p>
              <div className="space-y-2">
                <button onClick={() => navigate('/lesson-builder')} className="w-full flex items-center justify-between px-3 py-2 bg-white border border-[#EBE7E0] hover:border-[#14213D]/40 rounded-md text-sm text-[#14213D] transition-colors" data-testid="ai-lesson-builder">
                  <span className="inline-flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" />Build a Lesson</span>
                  <ArrowRight className="h-3.5 w-3.5 opacity-50" />
                </button>
                <button onClick={() => navigate('/rubric-generator')} className="w-full flex items-center justify-between px-3 py-2 bg-white border border-[#EBE7E0] hover:border-[#14213D]/40 rounded-md text-sm text-[#14213D] transition-colors" data-testid="ai-rubric">
                  <span className="inline-flex items-center gap-2"><CheckSquareIcon />Generate Rubric</span>
                  <ArrowRight className="h-3.5 w-3.5 opacity-50" />
                </button>
                <button onClick={() => navigate('/essay-grader-index')} className="w-full flex items-center justify-between px-3 py-2 bg-white border border-[#EBE7E0] hover:border-[#14213D]/40 rounded-md text-sm text-[#14213D] transition-colors" data-testid="ai-essay-grader">
                  <span className="inline-flex items-center gap-2"><FileText className="h-3.5 w-3.5" />Grade an Essay</span>
                  <ArrowRight className="h-3.5 w-3.5 opacity-50" />
                </button>
              </div>
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-3 gap-3" data-testid="dashboard-stats">
              <StatPill label="Units" value={stats.units} />
              <StatPill label="Grades" value={stats.grades} />
              <StatPill label="Sources" value={stats.sources} />
            </div>
          </div>
        </div>

        {/* Subject hubs */}
        <ModuleCard title="Browse by subject" testid="subject-hubs">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {SUBJECT_HUBS.map((s) => {
              const Icon = s.icon;
              return (
                <button
                  key={s.label}
                  onClick={() => navigate(s.path)}
                  className="group text-left bg-white border border-[#EBE7E0] rounded-md p-4 hover:border-[#14213D]/30 hover:shadow-md transition-all"
                  data-testid={`subject-hub-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="h-5 w-5 text-[#7A93AC] group-hover:text-[#14213D] transition-colors mb-3" strokeWidth={1.5} />
                  <h4 className="font-serif-display text-base text-[#14213D] mb-1">{s.label}</h4>
                  <p className="text-xs text-[#5A6577] leading-relaxed line-clamp-2">{s.desc}</p>
                </button>
              );
            })}
          </div>
        </ModuleCard>

        {/* Recently Added + Spotlight */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
          <div className="lg:col-span-8">
            <ModuleCard title="Recently added lessons" viewAllPath="/dashboard/9" testid="recent-lessons">
              {loading ? (
                <p className="text-sm text-[#5A6577]">Loading…</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {recentUnits.map((u) => (
                    <ResourceMiniCard key={u.id} unit={u} onOpen={() => navigate(`/unit/${u.id}`)} />
                  ))}
                </div>
              )}
            </ModuleCard>
          </div>

          <div className="lg:col-span-4 space-y-6">
            <ModuleCard title="Primary source spotlight" viewAllPath="/primary-sources-index" testid="ps-spotlight">
              <div className="relative h-32 rounded-md overflow-hidden mb-3">
                <img
                  src="https://images.pexels.com/photos/2355408/pexels-photo-2355408.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Historical documents"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <p className="text-xs uppercase tracking-[0.15em] text-[#D4AF37] font-semibold mb-1.5">957 sources indexed</p>
              <h4 className="font-serif-display text-base text-[#14213D] leading-tight mb-2">Annotated documents from every era</h4>
              <p className="text-xs text-[#5A6577] leading-relaxed">Search the full library by era, theme, or grade — and annotate inline.</p>
            </ModuleCard>

            <ModuleCard title="Interactive maps" viewAllPath="/maps" testid="maps-preview">
              <div className="relative h-32 rounded-md overflow-hidden mb-3 bg-[#E2E8F0]">
                <div className="absolute inset-0 opacity-30"
                  style={{ backgroundImage: "url('https://images.unsplash.com/photo-1524661135-423995f22d0b?crop=entropy&cs=srgb&fm=jpg&q=85&w=600')", backgroundSize: 'cover', backgroundPosition: 'center' }} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#14213D]/40 to-transparent" />
              </div>
              <p className="text-xs uppercase tracking-[0.15em] text-[#D4AF37] font-semibold mb-1.5">GIS-grade overlays</p>
              <h4 className="font-serif-display text-base text-[#14213D] leading-tight mb-2">Layered historical atlases</h4>
              <p className="text-xs text-[#5A6577] leading-relaxed">Slide through time on era-specific overlays — borders, trade routes, empires.</p>
            </ModuleCard>
          </div>
        </div>

        {/* Browse by Era */}
        <div className="mt-6">
          <ModuleCard title="Browse by era" testid="browse-by-era">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {ERAS.map((era) => (
                <button
                  key={era.label}
                  onClick={() => navigate(`/dashboard/${era.grade}`)}
                  className="group text-left bg-white border border-[#EBE7E0] rounded-md p-3 hover:border-[#14213D]/30 hover:shadow-sm transition-all"
                  data-testid={`era-${era.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="w-6 h-1 mb-2 rounded-full" style={{ background: era.color }} />
                  <h4 className="font-serif-display text-sm text-[#14213D] leading-tight">{era.label}</h4>
                  <p className="text-[11px] text-[#5A6577] mt-1 tracking-wide">{era.subtitle}</p>
                </button>
              ))}
            </div>
          </ModuleCard>
        </div>
      </div>
    </AppShell>
  );
}

const StatPill = ({ label, value }) => (
  <div className="bg-white border border-[#EBE7E0] rounded-md p-3 text-center">
    <p className="font-serif-display text-xl text-[#14213D] leading-none">{value}</p>
    <p className="text-[11px] uppercase tracking-[0.18em] text-[#5A6577] mt-1.5">{label}</p>
  </div>
);

// Tiny inline icon — avoids a second import path
const CheckSquareIcon = () => (
  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 11 12 14 22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);
